import { supabase } from '@/lib/supabase';
import { PaymentAgreement } from '@/components/dashboard/agreements/PaymentAgreementTypes';

const STORAGE_KEY = 'legal_notifications_agreements_v1';
export const ADMIN_CONTACT_STORAGE_KEY = 'legal_notifications_admin_contact_v1';

export interface AdminContactSettings {
  copropiedad_nombre: string;
  copropiedad_nit: string;
  copropiedad_logo_url?: string;
  copropiedad_logo_data?: string;
  administrador_nombre: string;
  administrador_whatsapp: string;
  administrador_telefono: string;
  administrador_email: string;
  administrador_horario: string;
  administrador_oficina: string;
}

export const DEFAULT_ADMIN_CONTACT_SETTINGS: AdminContactSettings = {
  copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
  copropiedad_nit: '900.852.147-3',
  copropiedad_logo_url: '',
  copropiedad_logo_data: '',
  administrador_nombre: 'Administración Torres del Parque',
  administrador_whatsapp: '3184567890',
  administrador_telefono: '(601) 745-8900 Ext. 102',
  administrador_email: 'cartera@torresdelparque.com',
  administrador_horario: 'Lunes a Viernes 8:00 AM - 5:00 PM | Sábados 8:00 AM - 12:00 M',
  administrador_oficina: 'Oficina de Administración - Torre 1, Piso 1'
};

export interface AgreementAuditPayload {
  agreement_id: string;
  event: 'agreement_created' | 'opened_by_debtor' | 'otp_generated' | 'signed_digitally' | 'payment_received' | 'status_changed';
  actor_type: 'debtor' | 'admin' | 'gateway_wompi' | 'gateway_nequi' | 'system';
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
}

export class PaymentAgreementDatabaseService {
  /**
   * Obtiene la configuración de canales de contacto de la administración ingresada por el administrador
   */
  static getAdminContactSettings(): AdminContactSettings {
    try {
      const stored = localStorage.getItem(ADMIN_CONTACT_STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_ADMIN_CONTACT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.warn('[DB] Error parsing admin contact settings:', e);
    }
    return DEFAULT_ADMIN_CONTACT_SETTINGS;
  }

  /**
   * Guarda los datos de contacto ingresados por el administrador
   * y los propaga a los acuerdos vigentes
   */
  static async saveAdminContactSettings(settings: AdminContactSettings): Promise<void> {
    try {
      localStorage.setItem(ADMIN_CONTACT_STORAGE_KEY, JSON.stringify(settings));

      // Actualizar todos los acuerdos para reflejar los nuevos datos de administración
      const agreements = await this.getAllAgreements();
      if (agreements && agreements.length > 0) {
        const updated = agreements.map((a) => ({
          ...a,
          copropiedad_nombre: settings.copropiedad_nombre || a.copropiedad_nombre,
          copropiedad_nit: settings.copropiedad_nit || a.copropiedad_nit,
          copropiedad_logo_url: settings.copropiedad_logo_url !== undefined ? settings.copropiedad_logo_url : a.copropiedad_logo_url,
          copropiedad_logo_data: settings.copropiedad_logo_data !== undefined ? settings.copropiedad_logo_data : a.copropiedad_logo_data,
          administrador_nombre: settings.administrador_nombre || a.administrador_nombre,
          administrador_whatsapp: settings.administrador_whatsapp || a.administrador_whatsapp,
          administrador_telefono: settings.administrador_telefono || a.administrador_telefono,
          administrador_email: settings.administrador_email || a.administrador_email,
          administrador_horario: settings.administrador_horario || a.administrador_horario,
          administrador_oficina: settings.administrador_oficina || a.administrador_oficina
        }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        for (const ag of updated) {
          await this.persistAgreement(ag);
        }
      }
    } catch (err) {
      console.warn('[DB] Error saving admin contact settings:', err);
    }
  }
  /**
   * Obtiene todos los acuerdos (de Supabase con fallback a localStorage)
   */
  static async getAllAgreements(): Promise<PaymentAgreement[]> {
    try {
      const { data, error } = await supabase
        .from('payment_agreements' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && Array.isArray(data) && data.length > 0) {
        // Sincronizar copia en localStorage para redundancia
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        return data as unknown as PaymentAgreement[];
      }
    } catch (err) {
      console.warn('[DB] Supabase query fallback to localStorage:', err);
    }

    // Fallback a localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignorar error de JSON parse
    }

    return [];
  }

  /**
   * Obtiene un acuerdo por su Magic Token o por su ID
   */
  static async getByTokenOrId(tokenOrId: string): Promise<PaymentAgreement | null> {
    const clean = tokenOrId.trim();
    try {
      const { data, error } = await supabase
        .from('payment_agreements' as any)
        .select('*')
        .or(`magic_token.eq.${clean},id.eq.${clean}`)
        .maybeSingle();

      if (!error && data) {
        return data as unknown as PaymentAgreement;
      }
    } catch (err) {
      console.warn('[DB] Error querying agreement from Supabase, attempting local fallback:', err);
    }

    // Fallback local
    const localList = await this.getAllAgreements();
    return (
      localList.find(
        (a) =>
          a.magic_token === clean ||
          a.id === clean ||
          (a.magic_token && clean.includes(a.magic_token))
      ) || null
    );
  }

  /**
   * Guarda o actualiza un acuerdo de pago en la base de datos Supabase
   * y registra auditoría legal (Ley 527 de 1999)
   */
  static async persistAgreement(
    agreement: PaymentAgreement,
    auditEvent?: AgreementAuditPayload
  ): Promise<boolean> {
    // 1. Guardar siempre en localStorage inmediatamente (optimistic UI)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const list: PaymentAgreement[] = stored ? JSON.parse(stored) : [];
      const index = list.findIndex((a) => a.id === agreement.id);
      if (index >= 0) {
        list[index] = agreement;
      } else {
        list.unshift(agreement);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (localErr) {
      console.warn('[DB] Error saving to localStorage:', localErr);
    }

    // 2. Persistir en la base de datos Supabase
    try {
      const payload = {
        id: agreement.id,
        copropiedad_id: agreement.copropiedad_id || 'default',
        copropiedad_nombre: agreement.copropiedad_nombre,
        copropiedad_nit: agreement.copropiedad_nit,
        copropiedad_logo_url: agreement.copropiedad_logo_url || null,
        administrador_nombre: agreement.administrador_nombre,
        administrador_telefono: agreement.administrador_telefono || null,
        administrador_email: agreement.administrador_email || null,
        administrador_whatsapp: agreement.administrador_whatsapp || null,
        administrador_horario: agreement.administrador_horario || null,
        administrador_oficina: agreement.administrador_oficina || null,
        unidad_privada: agreement.unidad_privada,
        propietario_nombre: agreement.propietario_nombre,
        propietario_id: agreement.propietario_id,
        telefono: agreement.telefono,
        email: agreement.email,
        deuda_total_inicial: agreement.deuda_total_inicial,
        capital_original: agreement.capital_original,
        intereses_originales: agreement.intereses_originales,
        porcentaje_condonacion_intereses: agreement.porcentaje_condonacion_intereses,
        intereses_condonados: agreement.intereses_condonados,
        deuda_pactada: agreement.deuda_pactada,
        cuota_inicial_monto: agreement.cuota_inicial_monto,
        cuota_inicial_porcentaje: agreement.cuota_inicial_porcentaje,
        cuota_inicial_pagada: agreement.cuota_inicial_pagada,
        cuota_inicial_fecha_limite: agreement.cuota_inicial_fecha_limite || null,
        cuota_inicial_referencia: agreement.cuota_inicial_referencia || null,
        saldo_a_financiar: agreement.saldo_a_financiar,
        numero_cuotas: agreement.numero_cuotas,
        valor_cuota_mensual: agreement.valor_cuota_mensual,
        tasa_financiacion_mensual: agreement.tasa_financiacion_mensual,
        dia_pago_mensual: agreement.dia_pago_mensual,
        fecha_inicio: agreement.fecha_inicio || null,
        estado: agreement.estado,
        cuotas_pagadas: agreement.cuotas_pagadas,
        cuotas_totales: agreement.cuotas_totales,
        total_recaudado: agreement.total_recaudado,
        saldo_restante: agreement.saldo_restante,
        proximo_vencimiento: agreement.proximo_vencimiento,
        mora_dias: agreement.mora_dias || 0,
        magic_token: agreement.magic_token,
        portal_url: agreement.portal_url,
        firmado_digitalmente_at: agreement.firmado_digitalmente_at || null,
        codigo_otp_firma: agreement.codigo_otp_firma || null,
        cuotas_detalle: agreement.cuotas_detalle || [],
        notas: agreement.notas || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('payment_agreements' as any)
        .upsert(payload, { onConflict: 'id' });

      if (error) {
        console.warn('[DB] Supabase upsert error (table might need creation):', error.message);
      } else {
        console.log(`[DB] Acuerdo ${agreement.id} sincronizado exitosamente en Supabase.`);
      }

      // 3. Registrar auditoría de firma digital o pago en la BD
      if (auditEvent) {
        await this.logAuditEvent(auditEvent);
      }

      return !error;
    } catch (err) {
      console.warn('[DB] Error connecting to Supabase:', err);
      return false;
    }
  }

  /**
   * Registra un evento inmutable en el log de auditoría
   */
  static async logAuditEvent(payload: AgreementAuditPayload): Promise<void> {
    try {
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
      await supabase.from('payment_agreement_audit_logs' as any).insert({
        agreement_id: payload.agreement_id,
        event: payload.event,
        actor_type: payload.actor_type,
        ip_address: payload.ip_address || 'client-browser',
        user_agent: payload.user_agent || userAgent,
        metadata: payload.metadata || {},
        created_at: new Date().toISOString()
      });
    } catch (err) {
      console.warn('[DB] Could not insert audit log:', err);
    }
  }
}
